/**
 * Sidebar drag-mode effects hook: three drag-mode-coupled `useEffect`s.
 *
 * Hook-order safety: the three effects must stay contiguous with NOTHING read
 * between them and their dependency arrays unchanged
 * (`[isDragMode]`, `[isCollapsed]`, `[searchQuery, isDragMode]`) to keep a
 * stable render-to-render hook order.
 */

import { useEffect } from 'react';
import { shouldExitDragModeOnClick } from '../dragMode';

interface UseDragModeEffectsArgs {
  isDragMode: boolean;
  setIsDragMode: (value: boolean) => void;
  isCollapsed: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  sidebarRef: React.RefObject<HTMLDivElement | null>;
}

export function useDragModeEffects({
  isDragMode,
  setIsDragMode,
  isCollapsed,
  searchQuery,
  setSearchQuery,
  sidebarRef,
}: UseDragModeEffectsArgs) {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (shouldExitDragModeOnClick(target, sidebarRef.current)) {
        setIsDragMode(false);
      }
    };

    if (isDragMode) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [isDragMode]);

  useEffect(() => {
    if (isCollapsed) {
      setSearchQuery('');
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (!searchQuery.trim() || !isDragMode) return;
    setIsDragMode(false);
  }, [searchQuery, isDragMode]);
}
